import React, { Fragment } from 'react';
import { EventSectionSaleProductReport } from '@/model/EventSectionSaleProductReport';
import { CustomTable, TableColumn } from '@/components/Table';
import { Button, InputText, SelectCustom } from '@/components';
import { toCurrency } from '@/helpers/masks/toCurrency';
import { EventSectionProductReport } from '@/model/EventSectionProductReport';
import { EventSectionMachineReport } from '@/model/EventSectionMachineReport';
import SuperCollapse from '@/components/sharedComponents/SuperCollapse';
import { ReportDropdownHeader } from '../../ReportDropdownHeader';
import { SaleProductSummaryCard } from '../../SaleProductSummaryCard';

interface StateProps {
  sections: EventSectionSaleProductReport[];
  allSections: EventSectionSaleProductReport[];
  selectedSectionIds?: string[];
  sectionId?: string;
  productName: string;
  serialNumber: string;
  posOptions?: { label: string; value: string }[];
  onChangeSectionIds?: (values: string[]) => void;
  onChangeSectionId?: (value: string) => void;
  onChangeProductName: (value: string) => void;
  onChangeSerialNumber: (value: string) => void;
  onFilter: () => void;
  onClearFilter: () => void;
  onExportPDF?: (sectionsToExport?: EventSectionSaleProductReport[]) => void;
}

export const SaleProductSectionsContent: React.FC<StateProps> = ({
  sections,
  allSections,
  selectedSectionIds,
  sectionId,
  productName,
  serialNumber,
  posOptions = [],
  onChangeSectionIds,
  onChangeSectionId,
  onChangeProductName,
  onChangeSerialNumber,
  onFilter,
  onClearFilter,
  onExportPDF,
}): JSX.Element => {
  const getColumns = (products: EventSectionProductReport[]): TableColumn<any>[] => {
    let showStock = false;
    let showCommission = false;

    const columns: TableColumn<any>[] = [
      {
        name: 'Produto',
        selector: row => row.productName,
      },
      {
        name: 'Vendidos',
        selector: row => row.productSold,
      },
      {
        name: 'Valor',
        selector: row => row.unitValue,
      },
      {
        name: 'Venda',
        selector: row => row.totalSaleValue,
      },
    ];

    // eslint-disable-next-line no-plusplus
    for (let p = 0; p < products.length; p++) {
      if (products[p].initialInventory > 0 || products[p].availableStock > 0) {
        showStock = true;
      }
      if (products[p].totalCommission > 0) {
        showCommission = true;
      }
      if (showStock && showCommission) {
        break;
      }
    }
    if (showCommission) {
      columns.push({
        name: 'Comissão',
        selector: row => row.totalCommission,
      });
      columns.push({
        name: 'Venda + Comissão',
        selector: row => row.totalSaleWithCommission,
      });
    }
    if (showStock) {
      columns.push({
        name: 'Estoque inicial',
        selector: row => row.initialInventory,
      });
      columns.push({
        name: 'Estoque disponível',
        selector: row => row.availableStock,
      });
    }
    return columns;
  };

  const getColumnMachines = (machines: EventSectionMachineReport[]): TableColumn<any>[] => {
    let showCourtesy = false;
    let showDiscount = false;
    let showCommission = false;
    const columns: TableColumn<any>[] = [
      {
        name: 'Serial',
        selector: row => row.serialNumber,
      },
      {
        name: 'Vendidos',
        selector: row => row.amountSold,
      },
    ];
    // eslint-disable-next-line no-plusplus
    for (let m = 0; m < machines.length; m++) {
      if (machines[m].courtesies > 0) {
        showCourtesy = true;
      }
      if (machines[m].discount > 0) {
        showDiscount = true;
      }
      if (machines[m].totalCommission > 0) {
        showCommission = true;
      }
      if (showCourtesy && showDiscount && showCommission) {
        break;
      }
    }
    if (showCourtesy) {
      columns.push({
        name: 'Cortesias',
        selector: row => row.courtesies,
      });
    }
    if (showDiscount) {
      columns.push({
        name: 'Desconto',
        selector: row => row.discount,
      });
    }
    columns.push({
      name: 'Venda',
      selector: row => row.totalSaleValue,
    });
    if (showCommission) {
      columns.push({
        name: 'Comissão',
        selector: row => row.totalCommission,
      });
      columns.push({
        name: 'Venda + Comissão',
        selector: row => row.totalSaleWithCommission,
      });
    }
    return columns;
  };

  const sectionOptions = (allSections.length > 0 ? allSections : sections).map(sec => ({
    label: sec.name,
    value: sec.id || (sec as any).sectionId,
  }));

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      onFilter();
    }
  };

  let activeSectionIds: string[] = [];
  if (selectedSectionIds && selectedSectionIds.length > 0) {
    activeSectionIds = selectedSectionIds;
  } else if (sectionId) {
    activeSectionIds = [sectionId];
  }

  let currentSelectValue: string[] = [];
  if (selectedSectionIds) {
    currentSelectValue = selectedSectionIds;
  } else if (sectionId) {
    currentSelectValue = [sectionId];
  }

  const displayedSections =
    activeSectionIds.length > 0
      ? sections.filter(sec => {
          const secId = String(sec.id || (sec as any).sectionId || '');
          return activeSectionIds.some(id => String(id) === secId);
        })
      : sections;

  const isFiltered = Boolean(
    activeSectionIds.length > 0 || productName.trim() || serialNumber.trim(),
  );

  return (
    <Fragment>
      {/* Container de Filtros com Flexbox e Botões alinhados à direita na linha abaixo */}
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: '20px 24px',
          marginBottom: '24px',
          border: '1px solid #e9ecef',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.03)',
        }}
      >
        <h6
          style={{
            fontSize: '14px',
            fontWeight: 600,
            color: '#495057',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '16px',
          }}
        >
          Filtros de Pesquisa
        </h6>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ flex: '1 1 250px' }}>
              <SelectCustom
                name="sectionIds"
                label="Setor (selecione um ou mais)"
                placeholder="Todos os setores"
                value={currentSelectValue}
                onChange={(selected: any) => {
                  let ids: string[] = [];
                  if (Array.isArray(selected)) {
                    ids = selected.map((item: any) => item.value);
                  } else if (selected?.value) {
                    ids = [selected.value];
                  }
                  if (onChangeSectionIds) {
                    onChangeSectionIds(ids);
                  } else if (onChangeSectionId) {
                    onChangeSectionId(ids[0] || '');
                  }
                }}
                options={sectionOptions}
                isMulti
                isClearable
              />
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <InputText
                name="filterProductName"
                label="Filtrar por Produto"
                placeholder="Digite o nome do produto"
                value={productName}
                onChange={e => onChangeProductName(e.target.value)}
                keyDow={handleKeyDown}
              />
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <SelectCustom
                name="filterSerialNumber"
                label="Filtrar por Serial POS"
                placeholder="Digite ou selecione a POS"
                value={serialNumber}
                onChange={(option: any) => onChangeSerialNumber(option?.value || '')}
                options={posOptions}
                isCreatable
                isClearable
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <Button title="Pesquisar" theme="blue" onClick={onFilter} />
            <Button title="Limpar" theme="red" onClick={onClearFilter} />
            {onExportPDF && (
              <Button
                title="Exportar PDF"
                theme="outlineDark"
                onClick={() => onExportPDF && onExportPDF(displayedSections)}
              />
            )}
          </div>
        </div>
      </div>

      {activeSectionIds.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '16px',
            padding: '10px 14px',
            backgroundColor: '#e8f4fd',
            borderRadius: '8px',
            border: '1px solid #b6d4fe',
            color: '#1b4880',
            fontSize: '13px',
          }}
        >
          <strong>Setores filtrados ({displayedSections.length}):</strong>
          <span>
            {sectionOptions
              .filter(opt => activeSectionIds.includes(opt.value as string))
              .map(opt => opt.label)
              .join(', ')}
          </span>
        </div>
      )}

      <SaleProductSummaryCard
        title="Resumo dos Setores"
        items={displayedSections}
        totalItemsCount={allSections.length > 0 ? allSections.length : sections.length}
        unitName={{ singular: 'setor', plural: 'setores' }}
        isFiltered={isFiltered}
      />

      {displayedSections.length === 0 && (
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '40px 24px',
            textAlign: 'center',
            border: '1px solid #e9ecef',
            color: '#6c757d',
            marginBottom: '24px',
          }}
        >
          Nenhum setor encontrado para os filtros selecionados.
        </div>
      )}

      {displayedSections.map(data => (
        <div key={data.id} className="mb-3">
          <SuperCollapse
            title={
              <ReportDropdownHeader
                title={data.name}
                summary={data.summary}
                payments={data.payments}
              />
            }
            content={
              <div className="p-3 bg-white rounded">
                {data.categories.map(category => (
                  <div key={category.id} className="mb-4">
                    <h6 className="font-weight-bold mb-2 text-secondary">{category.name}</h6>
                    <CustomTable
                      theme="secondaryWithoutBorder"
                      numberRowsPerPage={0}
                      progressPending={false}
                      columns={getColumns(category.products)}
                      data={
                        category.products.map(product => ({
                          productName: product.name,
                          productSold: product.amountSold,
                          initialInventory: product.initialInventory,
                          availableStock: product.availableStock,
                          unitValue: toCurrency(product.unitValue),
                          totalSaleValue: toCurrency(product.totalSaleValue),
                          totalCommission: toCurrency(product.totalCommission),
                          totalSaleWithCommission: toCurrency(product.totalSaleWithCommission),
                        })) || []
                      }
                    />
                  </div>
                ))}

                {data.machines && data.machines.length > 0 && (
                  <div className="mb-3">
                    <h6 className="font-weight-bold mb-2 text-secondary">POS</h6>
                    <CustomTable
                      theme="secondaryWithoutBorder"
                      numberRowsPerPage={0}
                      progressPending={false}
                      columns={getColumnMachines(data.machines)}
                      data={
                        data.machines.map(machine => ({
                          serialNumber: machine.serialNumber,
                          amountSold: machine.amountSold,
                          courtesies: machine.courtesies,
                          discount: toCurrency(machine.discount),
                          totalSaleValue: toCurrency(machine.totalSaleValue),
                          totalCommission: toCurrency(machine.totalCommission),
                          totalSaleWithCommission: toCurrency(machine.totalSaleWithCommission),
                        })) || []
                      }
                    />
                  </div>
                )}
              </div>
            }
          />
        </div>
      ))}
    </Fragment>
  );
};
